import React, { FC, PropsWithChildren, useState, useReducer, useEffect, useCallback } from 'react';
import { action } from 'typesafe-actions';
import * as R from 'ramda';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { Box } from '@dxb-web-ui-framework/design-system';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { getLimitDateFormat, mappingTransactionType } from '../../../../content-composer/src/utils';
import { accountService, transactionService } from '../../../../../channels/secure-web/services';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { ECOMMBXLoader } from '../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import { AccountDetails } from '../../domain/Account/entities';
import { useAccountListAccounts, useAccountListLoadingState } from '../AccountList/hooks';
import { AccountDetailsContext, AccountDetailsDispatchContext } from './AccountDetailsContext';
import { defaultAccountDetailsState, initialAccountExtraitTransactionListFilters } from './defaultState';
import { AccountDetailsReducer } from './AccountDetailsReducer';
import { AccountDetailsContextActionTypes, AccountDetailsProviderProps } from './types';
import { dateValue } from './../../../../transaction/src/contexts/AccountExtraitTransactions/utils';

const AccountDetailsProvider: FC<PropsWithChildren<AccountDetailsProviderProps>> = ({ children }) => {
  // Get initial state values
  const [timestamp] = useState(new Date().toISOString());
  const [accountTransactionsTimestamp, setAccountTransactionsTimestamp] = useState(new Date().toISOString());

  const [state, dispatch] = useReducer(AccountDetailsReducer, defaultAccountDetailsState);
  const { t } = useTranslation();
  const { accountId } = useParams();
  const customerId = useSelectedCustomerId();
  const { accounts } = useAccountListAccounts() ?? {};
  const isLoadingAccountsList = useAccountListLoadingState('isLoadingAccounts');
  const isAccountDetailsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.DepositAccountDetails);
  const isAccountExtraitAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.DepositAccountExtrait);

  useEffect(() => {
    if (accountId && accounts && !isLoadingAccountsList) {
      dispatch(
        action(
          AccountDetailsContextActionTypes.SetSelectedAccount,
          accounts.find(account => account?.accountId === Number(accountId)),
        ),
      );
    }
  }, [accountId, accounts, isLoadingAccountsList]);

  const shouldFetchAccountDetails =
    !isEmpty(accounts) && !isEmpty(customerId) && !isEmpty(state.selectedAccount) && isAccountDetailsAllowed;
  const {
    data: accountDetailsData,
    error: accountDetailsError,
    isLoading: isLoadingAccountDetails,
    mutate: accountDetailsRefetch,
  } = useInvokeSWRService<AccountDetails>(
    accountService.GetCustomerAccountDetails,
    state.selectedAccount === null
      ? {}
      : {
          customerId,
          accountId: state.selectedAccount?.accountId,
          meta: {
            timestamp,
          },
        },
    shouldFetchAccountDetails,
  );

  const shouldFetchAccountExtraitTransactionList =
    !isEmpty(accountDetailsData) && !isEmpty(state.selectedAccount) && isAccountExtraitAllowed;
  const {
    data: accountExtraitTransactionListData,
    error: accountExtraitTransactionListError,
    isLoading: isLoadingAccountExtraitTransactionList,
    mutate: accountExtraitTransactionListRefetch,
  } = useInvokeSWRService(
    transactionService.GetAccountExtraitTransactions,
    {
      accountNumber: state.selectedAccount?.accountNumber,
      ...state.accountExtraitTransactionListFilters,
      dateFrom: dateValue(
        new Date('2002-01-01'),
        state.accountExtraitTransactionListFilters.dateFrom,
        state.accountExtraitTransactionListFilters.fromAmount,
        state.accountExtraitTransactionListFilters.toAmount,
        true,
      ),
      dateTo: dateValue(
        new Date(),
        state.accountExtraitTransactionListFilters.dateTo,
        state.accountExtraitTransactionListFilters.fromAmount,
        state.accountExtraitTransactionListFilters.toAmount,
      ),
      transactionType: mappingTransactionType(t, state.accountExtraitTransactionListFilters.transactionType),
      meta: {
        accountTransactionsTimestamp,
      },
    },
    shouldFetchAccountExtraitTransactionList,
  );

  useEffect(() => {
    dispatch(action(AccountDetailsContextActionTypes.SetSelectedAccountDetails, accountDetailsData || null));
  }, [accountDetailsData]);

  const onAccountSelect = useCallback(
    (propertyValue: string | number | null, propertyName: 'accountNumber' | 'accountId' = 'accountId') => {
      if (propertyName === 'accountId' && typeof propertyValue === 'string') {
        propertyValue = parseInt(propertyValue);
      } else if (propertyName === 'accountNumber' && typeof propertyValue == 'number') {
        propertyValue = propertyValue.toString();
      }

      if (state.selectedAccount && propertyValue === state.selectedAccount[propertyName]) {
        return;
      }

      if (accounts && !isEmpty(accounts) && propertyValue) {
        const accountToSelect = R.find(account => account[propertyName] === propertyValue, accounts);

        // TODO: what will we do as error handling for this case
        if (!accountToSelect) {
          throw Error(`Account with ${propertyName} set to ${propertyValue} does not exist!`);
        }

        dispatch(action(AccountDetailsContextActionTypes.SetSelectedAccount, accountToSelect));
      } else {
        dispatch(action(AccountDetailsContextActionTypes.SetSelectedAccount, null));
      }
    },
    [accounts, state.selectedAccount],
  );

  // Callbacks for handling AccountExtrait filters and selected transaction
  const onTransactionListFiltersChange = useCallback(
    (filters, nextPage: number = 1, totalPages: number = 1) => {
      const newFilters = [
        { key: 'fromAmount', value: filters.fromAmount },
        { key: 'toAmount', value: filters.toAmount },
        {
          key: 'dateFrom',
          value: filters?.dateFrom ? getLimitDateFormat(filters.dateFrom, true) : null,
        },
        {
          key: 'dateTo',
          value: filters?.dateTo ? getLimitDateFormat(new Date(filters.dateTo)) : null,
        },
        { key: 'transactionType', value: filters.transactionType },
      ].reduce((acc, { key, value }) => {
        if (value?.toString()) {
          acc[key] = value;
        }

        return acc;
      }, {} as GenericObject);

      setAccountTransactionsTimestamp(new Date().toISOString());
      window.scrollTo(0, 0);

      dispatch(
        action(AccountDetailsContextActionTypes.setAccountExtraitTransactionListFilters, {
          ...newFilters,
          pageOffset: filters.pageSize
            ? Math.min(Math.max(nextPage - 1, 0), totalPages) * Math.max(filters.pageSize ?? 1, 1)
            : 0,
          pageSize: filters.pageSize ? state.accountExtraitTransactionListFilters.pageSize : 10,
        }),
      );
    },
    [state.accountExtraitTransactionListFilters.pageSize],
  );

  const onResetTransactionListFilters = useCallback(() => {
    dispatch(
      action(
        AccountDetailsContextActionTypes.setAccountExtraitTransactionListFilters,
        initialAccountExtraitTransactionListFilters,
      ),
    );
  }, []);

  const onSetLastPaginationPageVisited = useCallback((value: number) => {
    dispatch(action(AccountDetailsContextActionTypes.setLastPaginationPageVisited, value));
  }, []);

  const onRefetchAccountDetails = useCallback(() => {
    accountDetailsRefetch && accountDetailsRefetch();
  }, [accountDetailsRefetch]);

  const onRefetchAccountExtraitTransactionList = useCallback(() => {
    accountExtraitTransactionListRefetch && accountExtraitTransactionListRefetch();
  }, [accountExtraitTransactionListRefetch]);

  return (
    <AccountDetailsContext.Provider
      value={{
        ...state,
        onAccountSelect,
        accountExtraitTransactionListData,
        onTransactionListFiltersChange,
        onResetTransactionListFilters,
        onRefetchAccountDetails,
        onRefetchAccountExtraitTransactionList,
        loadingState: {
          isLoadingAccountDetails,
          isLoadingAccountExtraitTransactionList,
        },
        errorState: {
          accountDetailsError,
          accountExtraitTransactionListError,
        },
        onSetLastPaginationPageVisited,
      }}>
      <AccountDetailsDispatchContext.Provider value={dispatch}>
        {isLoadingAccountsList && (
          <Box style={{ height: '100vh', position: 'absolute', top: '50%', right: '50%' }}>
            <ECOMMBXLoader size='full' verticalSpacing='large' />
          </Box>
        )}
        {!isLoadingAccountsList && accounts && children}
      </AccountDetailsDispatchContext.Provider>
    </AccountDetailsContext.Provider>
  );
};

export { AccountDetailsProvider };
